import { Link } from "react-router-dom";
import ScrollReveal from "../components/marketing/ScrollReveal";
import { ArrowRight, AlertTriangle, Users, Ban, Scale, Skull, ChevronRight } from "lucide-react";
import { useLang } from "../contexts/LanguageContext";
import PageHead from "../components/PageHead";

const cases = [
  {
    id: "kinder",
    year: "2022",
    brand: "Ferrero",
    product: "Kinder Surprise, Schoko-Bons",
    crisis: "Contamination a la salmonelle",
    icon: AlertTriangle,
    color: "var(--kyra-rd)",
    bg: "rgba(176,48,37,0.06)",
    impact: [
      "Retrait massif dans toute l'Europe — plus de 3 000 tonnes de produits",
      "150+ cas confirmes de salmonellose, majoritairement des enfants",
      "Produits deja consommes ou stockes dans les foyers lors du rappel",
      "Communication par affichage en rayon — non ciblee, non tracable",
    ],
    problem: "Impossible de savoir quels foyers avaient encore les produits. Des millions de consommateurs n'ont jamais ete informes du rappel.",
    consequence: "Perte de confiance massive, cout estime a 40M EUR pour Ferrero, procedures judiciaires en cours dans plusieurs pays.",
    kyra: "KYRA aurait identifie chaque acheteur de Kinder via carte de fidelite et envoye une alerte nominative en moins de 2 heures — avant toute consommation supplementaire.",
  },
  {
    id: "lactalis",
    year: "2017",
    brand: "Lactalis",
    product: "Lait infantile Milumel, Picot, Taranis",
    crisis: "Contamination a la salmonelle — bebes touches",
    icon: Skull,
    color: "#8B1A10",
    bg: "rgba(139,26,16,0.06)",
    impact: [
      "38 nourrissons hospitalises en France",
      "12 millions de boites retirees dans 83 pays",
      "Produits retires... mais encore vendus en magasin pendant des semaines",
      "Des parents continuent de nourrir leurs bebes avec le produit contamine",
    ],
    problem: "Le rappel n'a pas ete maitrise jusqu'au consommateur final. Les pharmacies et magasins continuaient de vendre les lots concernes malgre le retrait officiel.",
    consequence: "Crise de reputation sans precedent pour Lactalis. Mise en examen du PDG. Renforcement legislatif sur les obligations de rappel.",
    kyra: "Avec KYRA, chaque parent ayant achete ces laits aurait recu un SMS et un email immediat — avec le numero de lot, le motif sanitaire et la conduite a tenir.",
  },
  {
    id: "findus",
    year: "2013",
    brand: "Findus",
    product: "Lasagnes, plats cuisines surgeles",
    crisis: "Fraude a la viande de cheval",
    icon: Ban,
    color: "var(--kyra-am)",
    bg: "rgba(168,107,16,0.06)",
    impact: [
      "Viande de cheval etiquetee comme viande de boeuf",
      "Fraude presente dans toute la chaine de sous-traitance",
      "Produits deja consommes par des millions de foyers",
      "Crise de confiance dans toute l'industrie agroalimentaire europeenne",
    ],
    problem: "Aucune tracabilite post-achat. Les produits frauduleux etaient dans les congelateurs des menages sans que personne ne puisse les identifier ni les prevenir.",
    consequence: "Effondrement des ventes de plats surgeles (-40% en 3 mois). Faillite de fournisseurs. Nouvelles reglementations europeennes sur la tracabilite.",
    kyra: "KYRA aurait croise les codes GTIN des lots concernes avec les achats clients et envoye une alerte ciblee — permettant un retrait effectif au niveau des menages.",
  },
  {
    id: "bluebell",
    year: "2015",
    brand: "Blue Bell Creameries",
    product: "Glaces et produits laitiers glaces",
    crisis: "Contamination a la Listeria",
    icon: Skull,
    color: "#6B1A1A",
    bg: "rgba(107,26,26,0.06)",
    impact: [
      "3 deces directement lies a la contamination",
      "10 hospitalisations graves",
      "Rappel total de TOUS les produits de la marque",
      "Fermeture de l'usine pendant plusieurs mois",
    ],
    problem: "Pas de lien direct avec les consommateurs exposes. Le rappel a ete publie mais les foyers a risque n'ont jamais ete contactes individuellement.",
    consequence: "Cout total estime a 100M USD. Destruction de reputation. Procedures penales contre les dirigeants.",
    kyra: "Un systeme comme KYRA aurait permis d'identifier et d'alerter en priorite les consommateurs immunodeprimes ou ages — ceux pour qui la Listeria est potentiellement mortelle.",
  },
  {
    id: "nestle",
    year: "2019-2024",
    brand: "Nestle",
    product: "Produits alimentaires divers (Buitoni, Maggi, etc.)",
    crisis: "Rappels multiples — contamination, allergenes, corps etrangers",
    icon: AlertTriangle,
    color: "var(--kyra-am)",
    bg: "rgba(168,107,16,0.06)",
    impact: [
      "Pizzas Buitoni : contamination E.coli — 2 deces d'enfants (2022)",
      "Rappels frequents sur allergenes non declares",
      "Communication massive mais non ciblee",
      "Consommateurs inondes de rappels generiques qu'ils ignorent",
    ],
    problem: "Communication massive mais non ciblee. Les consommateurs recoivent des rappels generiques qui ne les concernent pas, ce qui cree une fatigue du rappel — et quand un vrai risque survient, personne ne reagit.",
    consequence: "Erosion progressive de la confiance. Couts operationnels eleves pour des rappels inefficaces. Pertes humaines evitables.",
    kyra: "KYRA ne previent QUE les consommateurs qui ont reellement achete le produit concerne. Zero bruit. Alerte chirurgicale.",
  },
  {
    id: "pca",
    year: "2009",
    brand: "Peanut Corp. of America",
    product: "Beurre de cacahuete et derives",
    crisis: "Contamination a la salmonelle — fraude deliberee",
    icon: Scale,
    color: "#4A1A1A",
    bg: "rgba(74,26,26,0.06)",
    impact: [
      "714 personnes malades dans 46 etats americains",
      "9 deces confirmes",
      "Le plus grand rappel alimentaire de l'histoire des USA a l'epoque",
      "Le dirigeant condamne a 28 ans de prison",
    ],
    problem: "Chaine d'information completement fragmentee entre le fabricant, les distributeurs et les consommateurs. Des semaines avant que l'alerte n'atteigne les foyers.",
    consequence: "Faillite de l'entreprise. Condamnation penale historique. Refonte de la legislation alimentaire americaine (Food Safety Modernization Act).",
    kyra: "KYRA supprime cette fragmentation : de la publication du rappel a l'alerte au consommateur, le delai passe de semaines a heures.",
  },
];

export default function CasReelsPage() {
  const { t, lang } = useLang();
  return (
    <div data-testid="cas-reels-page">
      <PageHead title="Cas reels" description="Kinder, Lactalis, Buitoni, Findus : les crises alimentaires qui montrent pourquoi KYRA est necessaire." />
      {/* HERO */}
      <div className="relative overflow-hidden" style={{ padding: "130px 5.5% 72px", background: "var(--kyra-dk)", borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
        <div className="absolute pointer-events-none select-none" style={{ right: "-2%", bottom: "-15%", fontFamily: "'Playfair Display'", fontSize: "min(28vw, 380px)", fontWeight: 900, color: "rgba(255,255,255,0.02)", lineHeight: 0.8, letterSpacing: -8 }}>CRISES</div>        <div className="relative z-10">
          <ScrollReveal>
            <div className="flex gap-1.5 flex-wrap mb-6">
              <span className="text-[10px] font-medium uppercase tracking-[3px] px-2.5 py-1 rounded-sm" style={{ border: "1px solid rgba(176,48,37,0.4)", color: "#EF9A9A" }}>{t("casReels.kw1")}</span>
              <span className="text-[10px] font-medium uppercase tracking-[3px] px-2.5 py-1 rounded-sm" style={{ border: "1px solid rgba(255,255,255,0.12)", color: "rgba(255,255,255,0.4)" }}>{t("casReels.kw2")}</span>
            </div>
          </ScrollReveal>
          <ScrollReveal>
            <h1 className="font-black leading-[1.07] mb-5 max-w-[780px] text-white" style={{ fontFamily: "'Playfair Display'", fontSize: "clamp(34px,4.5vw,58px)", letterSpacing: -2 }}>
              {t("casReels.h1_1")}<br /><em style={{ color: "#EF9A9A" }}>{t("casReels.h1_2")}</em>
            </h1>
          </ScrollReveal>
          <ScrollReveal delay="d1">
            <p className="text-base font-light leading-[1.78] max-w-[600px] mb-6" style={{ color: "rgba(255,255,255,0.4)" }}>{t("casReels.sub")}</p>
          </ScrollReveal>
          <ScrollReveal delay="d2">
            <div className="flex items-center gap-4 flex-wrap">
              <div className="flex items-center gap-2 px-4 py-2 rounded-md" style={{ background: "rgba(176,48,37,0.15)" }}>
                <AlertTriangle className="w-4 h-4" style={{ color: "#EF9A9A" }} />
                <span className="text-xs font-medium" style={{ color: "#EF9A9A" }}>{t("casReels.badge1")}</span>
              </div>
              <div className="flex items-center gap-2 px-4 py-2 rounded-md" style={{ background: "rgba(255,255,255,0.06)" }}>
                <Users className="w-4 h-4" style={{ color: "rgba(255,255,255,0.5)" }} />
                <span className="text-xs font-medium" style={{ color: "rgba(255,255,255,0.5)" }}>{t("casReels.badge2")}</span>
              </div>
            </div>
          </ScrollReveal>
        </div>
      </div>

      {/* PATTERN COMMUN */}
      <section style={{ background: "var(--kyra-rd)", color: "#fff", padding: "56px 5.5%" }}>
        <ScrollReveal>
          <div className="max-w-[800px] mx-auto text-center">
            <p className="text-[10px] font-medium tracking-[4px] uppercase mb-4" style={{ color: "rgba(255,255,255,0.5)" }}>{t("casReels.structLabel")}</p>
            <h2 className="text-xl md:text-2xl font-black leading-tight mb-4" style={{ fontFamily: "'Playfair Display'" }}>
              {t("casReels.structTitle")}<br />{t("casReels.structTitle2")}
            </h2>
            <p className="text-sm font-light leading-relaxed max-w-[520px] mx-auto" style={{ color: "rgba(255,255,255,0.6)" }}>{t("casReels.structSub")}</p>
          </div>
        </ScrollReveal>
      </section>

      {/* CAS DETAILLES */}
      <section style={{ padding: "88px 5.5%" }}>
        <div className="mb-12">
          <ScrollReveal>
            <div className="flex items-center gap-2.5 mb-4 text-[10px] font-medium uppercase tracking-[4px]" style={{ color: "var(--kyra-rd)" }}>
              <span className="w-5 h-px" style={{ background: "var(--kyra-rd)" }} /> {t("casReels.crisesLabel")}
            </div>
          </ScrollReveal>
          <ScrollReveal>
            <h2 className="font-black leading-[1.1] mb-4" style={{ fontFamily: "'Playfair Display'", fontSize: "clamp(28px,3.8vw,46px)", letterSpacing: -1.5 }}>{t("casReels.crisesTitle")}</h2>
          </ScrollReveal>
        </div>

        <div className="flex flex-col gap-6">
          {cases.map((c, i) => (
            <ScrollReveal key={c.id} delay={i % 3 === 1 ? "d1" : i % 3 === 2 ? "d2" : ""}>
              <div
                data-testid={`case-${c.id}`}
                className="rounded-lg border overflow-hidden"
                style={{ borderColor: "var(--kyra-li)", background: "#fff" }}
              >
                {/* Header */}
                <div className="flex items-center gap-4 px-6 md:px-8 py-5" style={{ borderBottom: "1px solid var(--kyra-li)", background: c.bg }}>
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0" style={{ background: c.bg, border: `1px solid ${c.color}20` }}>
                    <c.icon className="w-5 h-5" style={{ color: c.color }} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-xs font-black tracking-tight" style={{ fontFamily: "'Playfair Display'", color: c.color }}>{c.year}</span>
                      <span className="text-[10px] tracking-wider uppercase font-medium" style={{ color: "var(--kyra-mu)" }}>{c.brand}</span>
                    </div>
                    <h3 className="text-base md:text-lg font-black tracking-tight leading-tight" style={{ fontFamily: "'Playfair Display'" }}>{c.crisis}</h3>
                  </div>
                  <span className="text-[10px] tracking-wider uppercase font-medium px-2.5 py-1 rounded-sm shrink-0 hidden md:inline-block" style={{ background: "var(--kyra-bg)", color: "var(--kyra-mu)", border: "1px solid var(--kyra-li)" }}>{c.product}</span>
                </div>

                {/* Body */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-0">
                  {/* Impact */}
                  <div className="p-6 md:p-8" style={{ borderRight: "1px solid var(--kyra-li)", borderBottom: "1px solid var(--kyra-li)" }}>
                    <span className="text-[9px] tracking-[3px] uppercase font-medium block mb-4" style={{ color: c.color }}>{t("casReels.impactLabel")}</span>
                    <div className="flex flex-col gap-2">
                      {c.impact.map((item, j) => (
                        <div key={j} className="flex gap-2 items-start">
                          <ChevronRight className="w-3 h-3 mt-0.5 shrink-0" style={{ color: c.color, opacity: 0.5 }} />
                          <span className="text-[12.5px] font-light leading-snug" style={{ color: "var(--kyra-mu)" }}>{item}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Probleme */}
                  <div className="p-6 md:p-8" style={{ borderRight: "1px solid var(--kyra-li)", borderBottom: "1px solid var(--kyra-li)" }}>
                    <span className="text-[9px] tracking-[3px] uppercase font-medium block mb-4" style={{ color: "var(--kyra-rd)" }}>{t("casReels.problemLabel")}</span>
                    <p className="text-[13px] font-light leading-[1.72] mb-4" style={{ color: "var(--kyra-tx)" }}>{c.problem}</p>
                    <span className="text-[9px] tracking-[3px] uppercase font-medium block mb-2" style={{ color: "var(--kyra-mu)" }}>Consequence</span>
                    <p className="text-[12px] font-light leading-relaxed" style={{ color: "var(--kyra-mu)" }}>{c.consequence}</p>
                  </div>

                  {/* KYRA Solution */}
                  <div className="p-6 md:p-8 relative" style={{ background: "rgba(27,67,50,0.03)", borderBottom: "1px solid var(--kyra-li)" }}>
                    <span className="text-[9px] tracking-[3px] uppercase font-medium block mb-4" style={{ color: "var(--kyra-gr)" }}>{t("casReels.kyraLabel")}</span>
                    <p className="text-[13px] font-normal leading-[1.72]" style={{ color: "var(--kyra-gr)" }}>{c.kyra}</p>
                    <div className="absolute bottom-3 right-4 opacity-5 pointer-events-none select-none" style={{ fontFamily: "'Playfair Display'", fontSize: 48, fontWeight: 900, color: "var(--kyra-gr)" }}>K</div>
                  </div>
                </div>
              </div>
            </ScrollReveal>
          ))}
        </div>
      </section>

      {/* CONCLUSION */}
      <section className="relative overflow-hidden" style={{ background: "var(--kyra-dk)", color: "#fff", padding: "88px 5.5%" }}>
        <div className="absolute pointer-events-none select-none" style={{ right: "-2%", bottom: "-10%", fontFamily: "'Playfair Display'", fontSize: "min(28vw,400px)", fontWeight: 900, lineHeight: 0.8, opacity: 0.03, letterSpacing: -6, color: "#fff" }}>KYRA</div>
        <div className="relative z-10 max-w-[720px]">
          <ScrollReveal>
            <div className="flex items-center gap-2.5 mb-4 text-[10px] font-medium uppercase tracking-[4px]" style={{ color: "rgba(183,228,199,0.5)" }}>
              <span className="w-5 h-px" style={{ background: "currentcolor" }} /> {t("casReels.conclusionLabel")}
            </div>
          </ScrollReveal>
          <ScrollReveal>
            <h2 className="font-black leading-[1.1] mb-4 text-white" style={{ fontFamily: "'Playfair Display'", fontSize: "clamp(28px,3.8vw,46px)", letterSpacing: -1.5 }}>
              {t("casReels.conclusionTitle1")}<br /><em style={{ color: "var(--kyra-gl)" }}>{t("casReels.conclusionTitle2")}</em>
            </h2>
          </ScrollReveal>
          <ScrollReveal delay="d1">
            <p className="text-base font-light leading-[1.78] mb-8" style={{ color: "rgba(255,255,255,0.45)" }}>{t("casReels.conclusionSub")}</p>
          </ScrollReveal>
          <ScrollReveal delay="d2">
            <div className="flex gap-3 flex-wrap">
              <Link to="/contact" className="no-underline bg-white px-7 py-3.5 rounded-sm text-[13px] font-medium transition-opacity hover:opacity-90 inline-block" style={{ color: "var(--kyra-dk)" }}>
                {t("casReels.ctaBtn1")}
              </Link>
              <Link to="/solutions" className="no-underline px-7 py-3.5 rounded-sm text-[13px] transition-all hover:text-white inline-block" style={{ border: "1px solid rgba(255,255,255,0.16)", color: "rgba(255,255,255,0.5)" }}>
                {t("casReels.ctaBtn2")} <ArrowRight className="inline w-3 h-3 ml-1" />
              </Link>
            </div>
          </ScrollReveal>
        </div>
      </section>
    </div>
  );
}
